import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Document, DocumentWriter, FieldDataType, QueryParams, View } from 'src/app/modules/core/store/models';
import { Dataset, DatasetSubtype } from 'src/app/modules/core/store/models/dataset.model';
import { GUID_EMPTY } from '@app/core/constants';
import { TranslateService } from '@ngx-translate/core';
import { DataApiService } from '../../services/data/data-api.service';
import * as copActions from '../cop/cop.actions';
import * as copSelectors from '../cop/cop.selectors';
import * as rsdActions from './rsd.actions';
import * as rsdSelectors from './rsd.selectors';
import * as listsActions from '../lists/lists.actions';
import * as impactedActions from '../impacted-assets/impacted-assets.actions';
import { MapStateService } from '../../services';
import { v4 as uuidv4 } from 'uuid';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { DatasetRelatedService } from 'src/app/modules/management/services/dataset-related.service';
import { DataApiService as MainDataApiService } from 'src/app/modules/management/store/data/data-api.service';
import * as fromdata from 'src/app/modules/management/store/data';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import moment from 'moment';
import { LayerDocuments } from '../cop/models';
import { MapLayerQueryBuildService } from '../../services/data/map-layer-query-build.service';
import * as fromNotifications from 'src/app/modules/notifications/store/notifications.actions';
import { LabelTranslationService } from 'src/app/modules/core/languages/label-translation.service';
import { cloneDeep } from 'lodash-es';
import { DEFAULT_APP_CULTURE_CODE, TranslationRequest } from 'src/app/modules/core/languages/language.model';
import { RelatedViewsService } from '@app/modules/core/components/related-documents/services/related-views.service';
import { DatasetService } from '@app/modules/core/services/dataset.service';
import * as fromImpactedAssets from 'src/app/modules/cop/store/impacted-assets';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

export const getEditView = (dataset: Dataset, document: Document): View => {
  if (!dataset) {
    return null;
  }

  const workflow = dataset.workflow;

  const workflowEditViewName = `edit-${document.workflow_step_uid}`;
  let editView = workflow?.views.find(f => f.name === workflowEditViewName);
  if (!editView || editView.controls.length === 0 || editView.controls[0]?.controls.length === 0) {
    editView = workflow?.views.find(f => f.name === 'detail');
  }

  if (!editView) {
    return null;
  }

  const view = produce(editView, draftState => {
    dataset?.schema.fields
      ?.filter(f => f.dataType === FieldDataType.AutoIncrement)
      .map(f => {
        const index = draftState.controls.findIndex(x => x.dataSetFieldUId === f.uId);
        if (index > -1) {
          draftState.controls[index].configuration = { isReadOnly: true };
        }
      });
  });

  return view;
};

@Injectable()
export class RsdEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private dataApiService: DataApiService,
    private notify: SystemNotificationsService,
    private translateService: TranslateService,
    private mapStateService: MapStateService,
    private datasetApiService: DatasetApiService,
    private datasetRelatedService: DatasetRelatedService,
    private mainDataApiService: MainDataApiService,
    private relatedViewsService: RelatedViewsService,
    private labelTranslationService: LabelTranslationService,
    private datasetService: DatasetService
  ) {}

  setSelectedDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.setSelectedDocument),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(rsdSelectors.selectSelectedDocumentState),
        this.store$.select(copSelectors.selectHistoryTrackEnabledUIds)
      ),
      switchMap(([action, mapState, docState, historyTrackEnabledUIds]) => {
        const { layerUId, documentUId, isDrawerOpen, replaceStack, overrides } = action;
        const layer = overrides?.uId?.datasetUId
          ? (Object.values(mapState.layersSet).find(l => l.datasetUId === overrides?.uId?.datasetUId) ?? mapState.layersSet[layerUId])
          : mapState.layersSet[layerUId];
        const _datasetUId = overrides?.uId?.datasetUId ?? layer?.datasetUId;
        const _dataset = mapState.datasetsSet[_datasetUId];
        const _documentUId = overrides?.uId?.documentUId ?? documentUId;
        const showDocumentHistoryTrack = !!historyTrackEnabledUIds.find(uId => uId === documentUId);
        const shouldGetHistoryDocuments = layer?.displayOptions?.historyTrackEnabled || showDocumentHistoryTrack;

        const dataset$ = Boolean(_dataset)
          ? of(_dataset)
          : this.dataApiService.getDataset(_datasetUId, { includeWorkflow: true }).pipe(
              tap(dataset => {
                copActions.addDatasetToStore({ dataset });
              })
            );

        return dataset$.pipe(
          switchMap(dataset =>
            forkJoin({
              document:
                dataset.subtype === DatasetSubtype.HorizonLocationIntelRatings && !!mapState?.documentsSet[documentUId]?.external_id
                  ? this.dataApiService.getHorizonIntelRatingsDocument(_datasetUId, mapState?.documentsSet[documentUId].external_id)
                  : this.dataApiService.getDocument(_datasetUId, _documentUId),
              historyDocuments: (shouldGetHistoryDocuments
                ? this.dataApiService.getHistoryDocuments(_datasetUId, _documentUId, true).pipe(map(res => res.data))
                : of([])) as Observable<fromdata.HistoryDocuments[]>
            }).pipe(
              switchMap(({ document, historyDocuments }) => {
                document.__layerUId = layer?.uId ?? layerUId;
                document.__datasetUId = _datasetUId;
                document.__historyTrackEnabled = showDocumentHistoryTrack;

                if (Boolean(historyDocuments?.length)) {
                  document.__history = historyDocuments.slice(0, 10).reduce((acc, { toDocument: _doc }) => {
                    if (Boolean(_doc)) {
                      const { change_timestamp, geometry } = _doc;

                      if (Boolean(geometry)) {
                        acc.push({
                          change_timestamp,
                          geometry
                        });
                      }
                    }

                    return acc;
                  }, []);
                }

                if (Boolean(overrides?.additionalDocumentData)) {
                  document = {
                    ...document,
                    ...overrides.additionalDocumentData
                  };
                }

                const editView = getEditView(dataset, document);

                if (
                  docState?.dataset.uId !== dataset.uId ||
                  docState?.document.uid !== document.uid ||
                  docState?.document.date_updated !== document.date_updated ||
                  docState?.layer?.displayOptions?.historyTrackEnabled !== layer?.displayOptions?.historyTrackEnabled
                ) {
                  return [
                    rsdActions.pushDocumentStateStack({
                      dataset,
                      layer,
                      document,
                      editView,
                      isDrawerOpen,
                      replaceStack,
                      edit: action.edit,
                      backAction: action.backAction
                    }),
                    copActions.addSelectedDocumentToStore({ document, clearExisting: true }),
                    impactedActions.showHideAssetsRSDView({ show: false }),
                    impactedActions.setClosestAssetDropdownValue({ val: null }),
                    fromNotifications.hideNotifications(),
                    fromImpactedAssets.setClosestAssetRequest({ closestAssetDto: null })
                  ];
                } else {
                  return [
                    copActions.addSelectedDocumentToStore({ document, clearExisting: true }),
                    impactedActions.showHideAssetsRSDView({ show: false }),
                    impactedActions.setClosestAssetDropdownValue({ val: null }),
                    fromNotifications.hideNotifications(),
                    fromImpactedAssets.setClosestAssetRequest({ closestAssetDto: null })
                  ];
                }
              }),
              catchError(err => of(copActions.setError({ err })))
            )
          )
        );
      })
    )
  );
  saveDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.saveDocument),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext), this.store$.select(fromUserProfile.selectProfileUser)),
      switchMap(([action, userContext, userProfile]) => {
        const { dataset, document } = action;

        const hasTranslations = this.datasetService.hasTranslations(dataset);
        let cultureCode = null;

        if (document.uid === GUID_EMPTY) {
          if (!!!hasTranslations) {
            if (userProfile?.culture?.cultureCode !== DEFAULT_APP_CULTURE_CODE) {
              cultureCode = userProfile?.culture?.cultureCode ?? DEFAULT_APP_CULTURE_CODE;
            }
          } else {
            cultureCode = userProfile?.culture?.fallbackCultureCode ?? DEFAULT_APP_CULTURE_CODE;
          }
        }

        const docToSave = fromdata.populateDefaults(document, dataset.workflow, userContext, cultureCode);
        if (document.uid === GUID_EMPTY) {
          return [rsdActions.createDocument({ dataset, document: docToSave })];
        } else {
          return [rsdActions.updateDocument({ dataset, document: docToSave })];
        }
      })
    )
  );

  createDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.createDocument),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([action, userContext]) => {
        const { dataset, document } = action;

        return this.dataApiService.createDocument(dataset.uId, document).pipe(
          switchMap(documents => {
            const actions = [];

            actions.push(rsdActions.saveDocumentComplete({ dataset, document: documents[0] }));
            if (MapLayerQueryBuildService.isUsingPollingQuery(null, dataset)) {
              actions.push(copActions.refreshLayerDocuments({ dataset, documents: documents }));
            }

            return actions;
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  updateDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.updateDocument),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { dataset, document } = action;

        return this.dataApiService.updateDocument(dataset.uId, document).pipe(
          switchMap(documents => {
            const actions = [];

            actions.push(rsdActions.saveDocumentComplete({ dataset, document: documents[0] }));
            if (MapLayerQueryBuildService.isUsingPollingQuery(null, dataset)) {
              actions.push(copActions.refreshLayerDocuments({ dataset, documents: [document] }));
            }
            return actions;
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  transitionWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.transitionWorkflow),
      withLatestFrom(this.store$.select(rsdSelectors.selectSelectedDocumentState)),
      switchMap(([action, documentState]) => {
        const { dataset, document } = action;

        return this.dataApiService.updateDocument(dataset.uId, document).pipe(
          switchMap(updatedDoc => {
            this.notify.success(this.translateService.instant('MAP.SAVE-DOCUMENT-COMPLETE'));
            return [rsdActions.transitionWorkflowComplete({ dataset, document })];
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.deleteDocument),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, currentMapState]) => {
        const { layer, dataset, document } = action;

        return this.dataApiService.deleteDocument(dataset.uId, document).pipe(
          switchMap(() => {
            this.notify.success(this.translateService.instant('MAP.DOCUMENT-DELETED'));

            const actions = [];
            if (MapLayerQueryBuildService.isUsingPollingQuery(layer, dataset)) {
              actions.push(copActions.removeLayerDocuments({ dataset, documents: [document], layerUId: layer.uId }));
            }
            actions.push(rsdActions.cancelEditDocument());
            actions.push(rsdActions.hideDrawer({ clearDocumentStack: true }));

            return actions;
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  newDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.newDocument),
      switchMap(action => {
        return [listsActions.clearCardsToStore(), rsdActions.setNewDocumentToStore()];
      })
    )
  );

  setNewDocumentDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.setNewDocumentDataset),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const schema = action.dataset?.schema;

        const workflow = action.dataset?.workflow;

        const uid = GUID_EMPTY;
        const newDocument = {
          uid,
          workflow_uid: workflow?.uId,
          workflow_step_uid: workflow?.workflowSteps[0].uId,
          external_id: uuidv4()
        };

        schema.fields
          .filter(f => f.dataType === FieldDataType.Geometry)
          .map(f => {
            newDocument[f.systemName] = null;
          });

        const editView = getEditView(action.dataset, document);

        return [
          rsdActions.pushDocumentStateStack({
            dataset: action.dataset,
            layer: null,
            document: newDocument,
            editView,
            isDrawerOpen: true,
            replaceStack: true
          }),
          rsdActions.editDocument()
        ];
      })
    )
  );

  // related docs

  initRelatedDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.initRelatedDocument),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([action, userContext]) => {
        return this.mainDataApiService
          .createMappedDocument(action.datasetUId, action.sourceDocument, action.targetDataset, action.defaultMapping)
          .pipe(
            map(result => {
              if (!!result) {
                return rsdActions.initRelatedDocumentComplete({ document: result });
              }
            }),
            catchError(err => of(copActions.setError({ err })))
          );
      })
    )
  );

  createRelatedDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.createRelatedDocument),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext), this.store$.select(rsdSelectors.selectSelectedDocumentState)),
      switchMap(([action, userContext, docState]) => {
        const fromDataset = docState.dataset;
        const forkActions = [];

        // make sure to create related parent fields
        let fromDatasetToSave = fromDataset;
        let fromRelatedField = this.datasetRelatedService.getRelatedParentFieldByDataset(fromDataset, action.dataset);
        if (!fromRelatedField) {
          fromRelatedField = this.datasetRelatedService.createRelatedParentField(fromDataset, action.dataset);
          fromDatasetToSave = produce(fromDataset, draftState => {
            draftState.schema.fields.push(fromRelatedField);
          });

          forkActions.push(
            this.datasetApiService.updateDataset(fromDatasetToSave).pipe(catchError(err => of(copActions.setError({ err }))))
          );
        }

        let toDatasetToSave = action.dataset;
        let toRelatedField = this.datasetRelatedService.getRelatedParentFieldByDataset(action.dataset, fromDataset);
        if (!toRelatedField) {
          toRelatedField = this.datasetRelatedService.createRelatedParentField(action.dataset, fromDataset);
          toDatasetToSave = produce(action.dataset, draftState => {
            draftState.schema.fields.push(toRelatedField);
          });
          forkActions.push(this.datasetApiService.updateDataset(toDatasetToSave).pipe(catchError(err => of(copActions.setError({ err })))));
        }

        if (forkActions.length == 0) {
          return [
            rsdActions.createDatasetRelatedFieldsComplete({
              fromDataset: fromDatasetToSave,
              toDataset: toDatasetToSave,
              toDocument: action.document
            })
          ];
        } else {
          return forkJoin(forkActions).pipe(
            mergeMap(([documents]) => {
              return [
                rsdActions.createDatasetRelatedFieldsComplete({
                  fromDataset: fromDatasetToSave,
                  toDataset: toDatasetToSave,
                  toDocument: action.document
                })
              ];
            })
          );
        }
      })
    )
  );

  createRelatedDocumentComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.createRelatedDocumentComplete),
      withLatestFrom(this.store$.select(rsdSelectors.selectSelectedDocumentState), this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, docState, mapState]) => {
        const fromDataset = docState.dataset;
        const fromDocument = docState.document;

        return this.mainDataApiService
          .relateDocuments(fromDataset.uId, fromDocument.uid, action.dataset.uId, [action.document.uid], false, true)
          .pipe(
            mergeMap(res => {
              const forkActions = [
                this.dataApiService
                  .getDocument(fromDataset.uId, fromDocument.uid)
                  .pipe(catchError(err => of(copActions.setError({ err })))),
                this.dataApiService
                  .getDocument(action.dataset.uId, action.document.uid)
                  .pipe(catchError(err => of(copActions.setError({ err }))))
              ];

              const afterActions = [];

              return forkJoin(forkActions).pipe(
                mergeMap(([fromDoc, toDoc]) => {
                  const actions = [];

                  actions.push(rsdActions.setCurrentDocumentState({ dataset: fromDataset, document: fromDoc }));

                  actions.push(rsdActions.setRelatedDocumentState({ dataset: action.dataset, document: toDoc }));
                  const writer = new DocumentWriter(fromDoc);
                  const completeSteps = fromDataset?.workflow.workflowSteps.filter(s => s.configuration?.completeStep === true);

                  var completeStep = fromDataset?.workflow.workflowSteps.find(s => s.configuration?.completeStep === true);

                  if (completeSteps.length >= 2) {
                    completeStep = completeSteps.find(s => s.uId === writer.workflowStepUId);
                  }

                  const layerDocuments: LayerDocuments = {};

                  let needsToUpdate = false;
                  if (!!completeStep) {
                    const currentStep = fromDataset?.workflow.workflowSteps.find(s => s.uId === fromDocument.workflow_step_uid);
                    if (!!currentStep && currentStep.actions?.find(a => a.destinationStepUId === completeStep.uId)) {
                      if (writer.workflowStepUId !== completeStep.uId) {
                        writer.workflowStepUId = completeStep.uId;
                        needsToUpdate = true;
                      }
                    }
                  }

                  const layersToUpdate = [];
                  const toLayers = Object.values(mapState.layersSet).filter(l => l.datasetUId === action.dataset.uId);
                  toLayers?.forEach(l => {
                    // use first layer found
                    if (!layersToUpdate.length) {
                      const dataset = mapState.datasetsSet[l.datasetUId];
                      actions.push(
                        copActions.zoomToDocument({
                          layer: l,
                          dataset: dataset,
                          document: toDoc
                        })
                      );
                      const editView = getEditView(dataset, toDoc);
                      afterActions.push(
                        rsdActions.pushDocumentStateStack({
                          dataset: dataset,
                          layer: l,
                          document: toDoc,
                          editView,
                          isDrawerOpen: true,
                          replaceStack: false
                        })
                      );
                    }
                    layerDocuments[l.uId] = [toDoc];
                    layersToUpdate.push(l.uId);
                  });

                  const fromLayers = Object.values(mapState.layersSet).filter(l => l.datasetUId === fromDataset.uId);
                  fromLayers.forEach(l => {
                    layerDocuments[l.uId] = [fromDoc];
                  });

                  if (needsToUpdate) {
                    actions.push(rsdActions.updateDocument({ dataset: fromDataset, document: writer.document }));
                  }

                  actions.push(...afterActions);

                  return actions;
                })
              );
            }),
            catchError(err => of(copActions.setError({ err })))
          );
      })
    )
  );

  /*loadRelatedDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.loadRelatedDocuments),
      withLatestFrom(this.store$.select(rsdSelectors.selectSelectedDocumentState)),
      switchMap(([action, docState]) => {
        const query = {
          pagination: {
            skip: 0,
            take: 100
          },
          filterGroup: null,
          fields: null
        } as DocumentQueryParams;

        rsdActions.setRelatedDocumentsComplete({ documents: [] });
        let type: DatasetType = null;
        let subtype: DatasetSubtype = null;

        if (!!action.datasetType && !!action.datasetSubtype) {
          type = action.datasetType;
          subtype = action.datasetSubtype;
        }
        else {
          const relatedTypes = this.datasetRelatedService.getRelatedDatasetTypeByDocument(docState.dataset, docState.document);
          type = relatedTypes?.type;
          subtype = relatedTypes?.subtype;
        }

        const request = {
          queryMatchRule: query,
          limitByType: type,
          limitBySubtype: subtype,
          includeChildren: false
        }
        return this.mainDataApiService
          .getAnyRelatedDocuments(docState.dataset?.uId, docState.document?.uid, request)
          .pipe(
            map(results => {
              if (!!!results?.data?.length && !!action.datasetUId) {
                return rsdActions.setRelatedDocumentsComplete({
                  documents: [{
                    datasetUId: action.datasetUId,
                    document: null
                  }]
                })
              }
              else {
                return rsdActions.setRelatedDocumentsComplete({ documents: results?.data?.length ? results.data : [] })
              }
            }
            ),
            catchError(err => of(copActions.setError({ err })))
          );
      })
    )
  );
*/
  loadRelatedDocumentCountsByDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.loadRelatedDocumentCountsByDataset),
      switchMap(action => {
        return this.mainDataApiService.getRelatedDocumentCountsByDataset(action.datasetUId, action.documentUId).pipe(
          map(results => rsdActions.setRelatedDocumentCountsByDatasetComplete({ results: results })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  loadRelatedDatasets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.loadRelatedDatasets),
      switchMap(action => {
        const queryParams: QueryParams = {
          skip: 0,
          take: 100,
          sortField: 'Name',
          sortDirection: 'Ascending',
          where: `type=${action.datasetType} AND subtype=${action.datasetSubtype} AND IsActive==true`
        };

        return this.datasetApiService.getDatasets(queryParams, true, true).pipe(
          map(results => rsdActions.loadRelatedDatasetsComplete({ datasets: results?.data })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  openRelatedTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.openRelatedTable),
      withLatestFrom(
        this.store$.select(rsdSelectors.selectRelatedDocumentCountsByDataset),
        this.store$.select(copSelectors.selectSelectedMapState)
      ),
      switchMap(([action, relatedCounts, mapState]) => {
        let datasetsSet = {};
        let layersSet = {};
        if (!!mapState) {
          datasetsSet = mapState.datasetsSet;
          layersSet = mapState.layersSet;
        }
        // create the list query and populate missing datasets
        return this.relatedViewsService.buildRelatedListQuery(relatedCounts, action.dataset, action.document, datasetsSet, layersSet).pipe(
          switchMap(relatedQuery => {
            if (relatedQuery?.data?.relatedDocumentsRequests?.length > 0) {
              return [
                rsdActions.hideDrawer({ clearDocumentStack: false }),
                listsActions.setRelatedListQueryToStore({ query: relatedQuery }),
                listsActions.showRelatedTableToStore()
              ];
            } else {
              return [];
            }
          })
        );
      })
    )
  );

  setRelatedDocumentState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.setRelatedDocumentState),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const layers = Object.values(mapState.layersSet).filter(l => action.dataset.uId === l.datasetUId);

        const writer = new DocumentWriter(action.document);
        writer.dateUpdated = moment();

        let newMapState = mapState;
        layers.forEach(l => {
          newMapState = produce(mapState, draftState => {
            const layerState = draftState.layerStates[l.uId];
            if (!!layerState) {
              layerState.documents[action.document.uid] = writer.document;
            }
          });
        });
        return [copActions.loadMapWithStateCompleteToStore({ mapWithState: newMapState })];
      })
    )
  );

  createDatasetRelatedFieldsComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.createDatasetRelatedFieldsComplete),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState), this.store$.select(fromAuth.selectUserContext)),
      switchMap(([action, mapState, userContext]) => {
        const newMapState = produce(mapState, draftState => {
          if (!!draftState.datasetsSet[action.fromDataset.uId]) {
            draftState.datasetsSet[action.fromDataset.uId] = action.fromDataset;
          }
          if (!!draftState.datasetsSet[action.toDataset.uId]) {
            draftState.datasetsSet[action.toDataset.uId] = action.toDataset;
          }
        });

        let toRelatedField = this.datasetRelatedService.getRelatedParentFieldByDataset(action.toDataset, action.fromDataset);

        const writer = new DocumentWriter(action.toDocument);
        writer.workflowUId = null;
        writer.workflowStepUId = null;
        writer.createdBy = null;
        writer.dateCreated = null;
        // small hack, we know that number of related items will be 1
        writer.setFieldValue(toRelatedField.systemName, 1);
        const docToSave = fromdata.populateDefaults(writer.document, action.toDataset.workflow, userContext);

        const forkActions = [];
        forkActions.push(
          this.dataApiService.createDocument(action.toDataset.uId, docToSave).pipe(catchError(err => of(copActions.setError({ err }))))
        );

        return forkJoin(forkActions).pipe(
          mergeMap(([documents]) => {
            if (!documents || !documents[0]) {
              const errorMessage = this.translateService.instant('MAP.SAVE-DOCUMENT-FAILED');
              this.notify.error(errorMessage);
              return [
                copActions.loadMapWithStateCompleteToStore({ mapWithState: newMapState }),
                copActions.setError({ err: new Error('Document failed to save on the server') })
              ];
            }

            const message = this.translateService.instant('MAP.SAVE-DOCUMENT-COMPLETE');
            this.notify.success(message);

            return [
              copActions.loadMapWithStateCompleteToStore({ mapWithState: newMapState }),
              rsdActions.createRelatedDocumentComplete({ dataset: action.toDataset, document: documents[0] })
            ];
          })
        );
      })
    )
  );

  openRelatedDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.openRelatedDocument),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        let relatedDataset = action.dataset;

        const layer = mapState.layersSet[action.layerUId];

        const newMapState = produce(mapState, draftState => {
          if (!!!draftState.datasetsSet[relatedDataset.uId]) {
            draftState.datasetsSet[relatedDataset.uId] = relatedDataset;
          }
        });

        const editView = getEditView(relatedDataset, action.document);

        return [
          copActions.loadMapWithStateCompleteToStore({ mapWithState: newMapState }),
          rsdActions.pushDocumentStateStack({
            dataset: relatedDataset,
            layer: !!layer ? layer : null,
            document: action.document,
            editView,
            isDrawerOpen: true,
            replaceStack: false
          })
        ];
      })
    )
  );

  closeRelatedDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.closeRelatedDocument),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        return [rsdActions.popDocumentStateStack()];
      })
    )
  );

  translateSelectedDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rsdActions.translateSelectedDocument),
      withLatestFrom(this.store$.select(rsdSelectors.selectSelectedDocumentState), this.store$.select(fromAuth.selectUserContext)),

      switchMap(([action, docState, userContext]) => {
        const dataset = docState.dataset;
        const document = docState.document;
        const strings: TranslationRequest[] = [];

        Object.keys(document).forEach(key => {
          const field = dataset.schema.fields.find(f => f.systemName === key);
          if (field?.dataType === FieldDataType.Text) {
            if (key !== 'address') {
              if (!!document[key] && document[key].trim().length > 0) {
                strings.push({
                  text: document[key],
                  id: field.systemName
                });
              }
            }
          }
        });

        return this.labelTranslationService.getTranslatedDocumentStrings(action.fromCode, action.toCode, strings).pipe(
          catchError(e => {
            console.error(e);
            return of(null);
          }),
          switchMap(results => {
            const updatedDocument = cloneDeep(document);
            if (!!results) {
              results.forEach((t, index) => {
                updatedDocument[t.id] = t.text;
              });
              updatedDocument[fromdata.SystemFieldNames.cultureCode] = action.toCode;
              updatedDocument[fromdata.SystemFieldNames.dateUpdated] = moment.utc();
            }
            return [
              rsdActions.translateSelectedDocumentComplete({
                document: updatedDocument,
                fromCode: action.fromCode,
                toCode: action.toCode
              })
            ];
          })
        );
      })
    )
  );
}
